import React, { PropsWithChildren } from 'react';
import {
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Donor, DonorContextProps } from './interfaces';
import { defaultDonorContext, deleteAccessTokens } from './donorLocalStorage';
import { useLoadDonor } from './useLoadDonor';
import { logoutFullStory } from '../../components/Wrappers/FullStory';
import { useLastLogin } from '../../components/LoginBottomSheet/LastLoginProvider';
import { useQueryClient } from '@tanstack/react-query';
import { clearQueriesExceptDonee, useDonorProfile } from '../../api';

export const DonorContext = createContext<{
    donorContext: DonorContextProps;
    setDonorContext: Dispatch<SetStateAction<DonorContextProps>>;
    loadDonor: (forcePull?: boolean | undefined) => unknown;
    logout: () => unknown;
}>({
    donorContext: defaultDonorContext(),
    setDonorContext: () => null,
    loadDonor: () => null,
    logout: () => null,
});

export const useDonorContext = () => useContext(DonorContext);

const DonorProvider: React.FC<
    PropsWithChildren & {
        donorEmailRef: React.MutableRefObject<Donor | undefined>;
        onDonorContextUpdated?: (donorContext: DonorContextProps) => void;
    }
> = ({ donorEmailRef, onDonorContextUpdated, children }) => {
    const { setForceHide } = useLastLogin();
    const [donorContext, setDonorContextState] = useState<DonorContextProps>(
        defaultDonorContext(),
    );
    const donorContextRef = useRef<DonorContextProps>(donorContext);
    const setDonorContext = useCallback<
        Dispatch<SetStateAction<DonorContextProps>>
    >(
        (nextContextOrUpdater) => {
            const nextDonorContext =
                typeof nextContextOrUpdater === 'function'
                    ? (
                          nextContextOrUpdater as (
                              prevState: DonorContextProps,
                          ) => DonorContextProps
                      )(donorContextRef.current)
                    : nextContextOrUpdater;

            donorContextRef.current = nextDonorContext;
            setDonorContextState(nextDonorContext);
            onDonorContextUpdated?.(nextDonorContext);
        },
        [onDonorContextUpdated],
    );
    const loadDonor = useLoadDonor();
    const { donorProfile } = useDonorProfile();
    const queryClient = useQueryClient();

    useEffect(() => {
        loadDonor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!donorProfile) return;
        donorEmailRef.current = donorProfile.donor;
    }, [donorProfile, donorEmailRef]);

    const logout = useCallback(() => {
        deleteAccessTokens();
        // drop every cached response for the signed-in donor: profile, gift
        // summary, recurring donations. The donee is public and stays loaded
        clearQueriesExceptDonee(queryClient);
        setDonorContext(defaultDonorContext());
        logoutFullStory();
        setForceHide(true);
    }, [queryClient, setDonorContext, setForceHide]);

    return (
        <DonorContext.Provider
            value={{ donorContext, setDonorContext, loadDonor, logout }}
        >
            {children}
        </DonorContext.Provider>
    );
};

export default React.memo(DonorProvider);
